<script lang="ts" setup>
import { useBrowserStore } from '@apps/main/new-desktop/core/stores';

import { AppSvgIcon, AppLinkButton, AppTooltip } from '@libs/ui';

import { FerryUpsellType } from '../../../models';

interface FerryCardFerryDetailsProps {
  vehicles: string[];
  allowVehicles: boolean;
  cabin: boolean;
  printMode: boolean;
}

const props = defineProps<FerryCardFerryDetailsProps>();

interface FerryCardFerryDetailsEmits {
  (event: 'change-upsell', upsellType: FerryUpsellType): void;
}

const emit = defineEmits<FerryCardFerryDetailsEmits>();

const browserStore = useBrowserStore();
</script>

<template>
  <div class="ferry-card-ferry-details">
    <!-- vehicle -->
    <div
      v-if="props.vehicles.length || props.allowVehicles"
      class="upsell-item"
      @click.stop="emit('change-upsell', FerryUpsellType.Vehicle)"
    >
      <div class="value">
        <div v-if="props.vehicles.length" class="vehicles">
          {{ `${props.vehicles.length} ${$t('planner.vehicle')}` }}
        </div>
        <AppLinkButton
          v-if="props.allowVehicles && !props.printMode"
          id="change-vehicle-category-button"
          class="edit-vehicles"
          :text="
            props.vehicles.length
              ? $t('planner.edit')
              : $t('planner.transport.add_vehicle')
          "
          size="s"
          @click="emit('change-upsell', FerryUpsellType.Vehicle)"
        />
      </div>
    </div>

    <!-- cabin -->
    <div v-if="props.cabin" class="upsell-item">
      <div class="value">
        <span>{{ $t('planner.cabin_seat_included') }}</span>
        <AppTooltip
          :position="
            browserStore.isSmallerThan1200px ? 'bottom-left' : 'bottom-right'
          "
        >
          <template #trigger>
            <AppSvgIcon name="infoCircle" size="18" />
          </template>
          <template #default>
            {{ $t('planner.can_be_changed_in_later_step') }}
          </template>
        </AppTooltip>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.ferry-card-ferry-details {
  display: flex;
  align-items: center;
  justify-content: center;

  @include for-less-or-equal-than-599-px {
    justify-content: space-between;
  }

  .upsell-item {
    display: flex;
    justify-content: flex-start;
    padding: 0 var(--spacing-16);
    border-right: solid 1px var(--color-overlay);
    @include for-less-or-equal-than-599-px {
      border-right: none;
      padding: 0;
    }

    .value {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      flex-direction: row;
      gap: var(--spacing-8);
      @include font-label-s-light;
      color: var(--color-neutral-70);

      @include for-less-or-equal-than-599-px {
        justify-content: space-between;
      }

      .description {
        display: flex;
        justify-content: flex-start;
        align-items: center;
        flex-direction: row;
        gap: var(--spacing-8);
      }

      .vehicles {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        white-space: wrap;

        @include for-less-or-equal-than-599-px {
          gap: var(--spacing-4);
          flex-direction: row;
          align-items: start;
        }
      }

      .edit-vehicles {
        color: var(--color-neutral-70);
      }
    }
  }
  .upsell-item:last-child {
    border-right: none;
  }
}
</style>
